<template>
  <payment-container v-if="devices.length" v-model="_selected" v-bind="containerProps">
    <img src="~/assets/images/integrations/square.png" class="max-h-8 max-w-14 opacity-85">

    <template #control>
      <div v-if="active" class="mt-4">
        <o-field-group>
          <select-field
            v-model="device_id"
            :options="devices"
          />
        </o-field-group>
      </div>

      <Teleport to="#modal-container">
        <div v-if="processing" class="z-[800000] fixed inset-0 h-screen w-screen bg-white flex items-center justify-center">
          <div class="">
            <img src="~/assets/images/integrations/square.png" class="h-5 mx-auto mb-8">

            <p class="text-2xl tracking-tighter font-bold text-center">
              {{ $currency.format(Number(amount)) }}
            </p>
            <p class="text-lg font-bold text-center">
              {{ $t('components.order.elements.payments.integration.square.terminal.continue') }}
            </p>

            <o-button flat size="lg" class="w-full mt-6 mb-12" @click="handleCancelTransaction">
              {{ $t('components.order.elements.payments.integration.square.terminal.cancel') }}
            </o-button>
          </div>
        </div>
      </Teleport>
    </template>
  </payment-container>
</template>

<script setup lang="ts">
import {
  OAlert,
} from '@/components/elements'

import {
  CheckboxField,
  SelectField,
} from '@/components/fields'

import PaymentContainer from '@/components/order/elements/payments/PaymentContainer.vue'
import { Integration } from '@/_api/models/integration'
import { OrderTerminalPaymentStatus } from '@/_api/models/order'

import { useIntegrationStore } from '~/stores/useIntegrationStore'

const { $api, $i18n, $currency } = useNuxtApp()

const integrationStore = useIntegrationStore()

interface Props {
  order: any
  amount: string | number
  tip?: number | string | null
  option: any
  selected?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  tip: null,
  selected: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: string): void
}>()

const _selected = computed({
  get: () => props.selected,
  set: (value: string) => emit('update:selected', value),
})

const active = computed(() => props.option.guid === props.selected)
const integration = computed(() => new Integration(props.option.data))

const processing = ref(false)
const device_id = ref<string | null>(null)
const checkout_id = ref<string | null>(null)
const resolveRef = ref<Function | null>(null)
const rejectRef = ref<Function | null>(null)

const containerProps = computed(() => ({
  label: $i18n.t('components.order.elements.payments.integration.square.terminal.label'),
  info: $i18n.t('integration.square.label'),
  option: props.option,
}))

const devices = computed(() => {
  const d = props.option.data.data.params.devices || []
  return d
    .filter((device: any) => device.status === 'PAIRED')
    .map((device: any) => ({
      value: device.device_id,
      text: device.name,
    }))
})

watch(devices, (newDevices) => {
  if (newDevices.length) {
    const square = integrationStore.square
    const stored_device = square.device

    const match = newDevices.find((item: any) => item?.value == stored_device)
    device_id.value = match ? stored_device : newDevices[0].value
  }
})

watch(device_id, (value) => {
  integrationStore.setSquareDevice(value)
})

async function process() {
  processing.value = true

  const response = await new Promise((resolve, reject) => {
    resolveRef.value = async (resp: any) => {
      const response = await $api.order.get(String(props.order.idOrder))
      resolve(response.data)
    }
    rejectRef.value = (resp: any) => reject(resp)

    addPayment()
  })

  return response
}

async function addPayment() {
  try {
    const result = await $api.order.addPaymentTerminal(String(props.order.idOrder), {
      guid: props.option.data.guid,
      amount: Number(props.amount),
      params: { device_id: device_id.value },
    })

    if (result.data) {
      checkout_id.value = result.data.id
    }

    setTimeout(() => {
      checkPayment()
    }, 1000)
  } catch (e) {
    processing.value = false
    rejectRef.value?.(e)
  }
}

async function handleCancelTransaction() {
  processing.value = false

  try {
    await $api.order.cancelPaymentTerminal(String(props.order.idOrder), {
      guid: props.option.data.guid,
      paymentId: String(checkout_id.value),
    })
    checkPayment(true)
  } catch (e) {
    console.log(e)
  }
}

async function checkPayment(force = false) {
  if (!processing.value && !force) {
    return false
  }

  try {
    const result = await $api.order.checkPaymentTerminal(String(props.order.idOrder), {
      guid: props.option.data.guid,
      paymentId: String(checkout_id.value),
    })

    if (result.data) {
      const status = result.data.status

      if (status === OrderTerminalPaymentStatus.pending || status === OrderTerminalPaymentStatus.processing) {
        setTimeout(() => {
          checkPayment()
        }, 1000)
      } else if (status === OrderTerminalPaymentStatus.complete) {
        processing.value = false
        resolveRef.value?.(result.data)
      } else {
        processing.value = false

        let message = $i18n.t('components.order.elements.payments.integration.square.terminal.failed')

        if (status === OrderTerminalPaymentStatus.cancelled) {
          message = $i18n.t('components.order.elements.payments.integration.square.terminal.cancel')
        } else if (status === OrderTerminalPaymentStatus.timeout) {
          message = $i18n.t('components.order.elements.payments.integration.square.terminal.timedOut')
        }

        rejectRef.value?.({ errors: { cancelled: message } })
      }
    }
  } catch (e) {
    console.log(e)
    processing.value = false
    rejectRef.value?.(e)
  }
}

defineExpose({ process })
</script>
